import React, { useMemo, useEffect, useState, useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import useRootSelector from 'common/hooks/useRootSelector';
import { Button, Typography } from '@material-ui/core';

import * as actions from 'common/components/PersonProfile/state/actions';
import * as selectors from 'common/components/PersonProfile/state/selectors';
import { FilterTypes, IFilterSettings } from 'common/interfaces/filter';
import tableFilters from 'common/messages/tableFilters';
import modalMessages from 'common/messages/modalTitles';
import commonMessages from 'common/messages/messages';
import tableHeaders from 'common/messages/tableHeaders';
import { automaticManualOptions } from 'common/constants/historyModal';
import { makeTableParams } from 'common/utils/http';
import useTimezoneMoment from 'common/hooks/useTimezoneMoment';
import { DEFAULT_DATE_TIME_FORMAT } from 'common/constants/dateFormats';
import { useAppDispatch } from 'store/hooks';
import { TableOrderByParams } from 'common/constants';
import { PeakModuleForNewPersonType } from 'common/interfaces/steps';
import { IHeadCell, ITableParams, ITableRow } from 'common/interfaces/table';

import { DialogComponent, Table } from 'common/components/index';

const headerOptions: IHeadCell[] = [
  {
    id: TableOrderByParams.DATE,
    label: <FormattedMessage {...tableHeaders.date} />,
    sort: true,
  },
  {
    id: 'email',
    label: <FormattedMessage {...tableHeaders.email} />,
    sort: true,
  },
  {
    id: 'from',
    label: <FormattedMessage {...tableHeaders.from} />,
    sort: true,
  },
  {
    id: 'to',
    label: <FormattedMessage {...tableHeaders.to} />,
    sort: true,
  },
  {
    id: TableOrderByParams.CAMPAIGN,
    label: <FormattedMessage {...tableHeaders.campaign} />,
    sort: true,
  },
  {
    id: TableOrderByParams.TYPE,
    label: <FormattedMessage {...tableHeaders.type} />,
    sort: true,
  },
];

interface IProps {
  isOpen: boolean;
  onClose: () => void;
  personId: number;
  onOpenEmailDetails: (id: string) => void;
  module?: PeakModuleForNewPersonType;
}

const EmailsHistoryModal = ({
  isOpen,
  onClose,
  personId,
  module,
  onOpenEmailDetails,
}: IProps): JSX.Element => {
  const dispatch = useAppDispatch();
  const [timezoneMoment] = useTimezoneMoment();

  const historyMeta = useRootSelector(selectors.selectPersonEmailsMeta(personId));
  const history = useRootSelector(selectors.selectPersonEmails(personId));
  const loading = useRootSelector(selectors.selectPersonEmailsLoading(personId));
  const createEmailActionResult = useRootSelector(
    selectors.selectPersonCreateEmailActionResult(personId),
  );

  const tableFilterSettings: IFilterSettings[] = useMemo(
    () => [
      {
        name: 'range',
        title: <FormattedMessage {...tableFilters.period} />,
        type: FilterTypes.DATE_RANGE,
        options: {
          startDate: null,
          endDate: null,
        },
      },
      {
        name: 'automatic',
        title: <FormattedMessage {...tableFilters.type} />,
        type: FilterTypes.SINGLE,
        options: automaticManualOptions,
      },
    ],
    [],
  );

  const rows: ITableRow[] = useMemo(() => {
    return history
      ?.map(emailItem => {
        return {
          id: emailItem.get('id'),
          cells: [
            {
              label: '',
              cellComponent: (
                <Button
                  variant="text"
                  className="btn-text"
                  type="button"
                  onClick={() => onOpenEmailDetails(emailItem.get('id'))}
                >
                  <Typography variant="h6" color="primary">
                    {timezoneMoment(emailItem.get('date')).format(DEFAULT_DATE_TIME_FORMAT)}
                  </Typography>
                </Button>
              ),
            },
            {
              label: emailItem.get('email'),
            },
            {
              label: emailItem.get('senderEmail'),
            },
            {
              label: emailItem.get('receiverEmail'),
            },
            {
              label: emailItem.getIn(['campaign', 'title']),
            },
            {
              label: (
                <FormattedMessage
                  {...(emailItem.get('automatic')
                    ? commonMessages.automaticLabel
                    : commonMessages.manualLabel)}
                />
              ),
            },
          ],
        };
      })
      .toJS();
  }, [history, onOpenEmailDetails, timezoneMoment]);

  const [tableParams, setTableParams] = useState(() =>
    makeTableParams(tableFilterSettings, null, {
      orderBy: TableOrderByParams.DATE,
      order: 'desc',
    }),
  );

  const onChangeTableParams = useCallback((tableProps: ITableParams): void => {
    setTableParams(tableProps);
  }, []);

  useEffect(() => {
    if (module) {
      dispatch(actions.fetchPersonEmails(personId, tableParams, module));
    }
  }, [dispatch, module, personId, tableParams]);

  useEffect(() => {
    if (createEmailActionResult && module) {
      dispatch(actions.fetchPersonEmails(personId, tableParams, module));
    }
  }, [createEmailActionResult, dispatch, module, personId, tableParams]);

  useEffect(() => {
    return () => {
      dispatch(actions.resetPersonEmailsAction(null, personId));
    };
  }, [dispatch, personId]);

  return (
    <DialogComponent
      isOpen={isOpen}
      onClose={onClose}
      title={<FormattedMessage {...modalMessages.emailHistory} />}
      size="xl"
      hideFooterButtons
    >
      <Table
        rows={rows}
        tableParams={tableParams}
        headerOptions={headerOptions}
        filters={tableFilterSettings}
        onChangeParams={onChangeTableParams}
        totalRows={historyMeta?.get('total')}
        page={historyMeta?.get('page')}
        isLoading={loading}
        hideToolbar
        showPerPageSelect
        hideSearchInput
      />
    </DialogComponent>
  );
};

export default EmailsHistoryModal;
